import { ecrireJson, lireJson, type Resultat } from './client';

/** Un document joint : le contenu voyage en base64, comme le navigateur sait le lire. */
export interface DocumentJoint {
  readonly nomFichier: string;
  readonly typeMime: string;
  readonly contenu: string;
}

/** Une habilitation cochée, avec sa catégorie (CACES) et son justificatif. */
export interface HabilitationChoisie {
  readonly code: string;
  readonly categorie?: string;
  readonly justificatif?: DocumentJoint;
}

/** Ce que la modale envoie pour créer la fiche. */
export interface SaisieTechnicien {
  readonly nom: string;
  readonly email: string;
  readonly telephone?: string;
  readonly dateEmbauche?: string;
  readonly role: string;
  readonly societe?: string;
  readonly actif: boolean;
  readonly photo?: DocumentJoint;
  readonly habilitations: ReadonlyArray<HabilitationChoisie>;
}

/** Une habilitation telle que le serveur la rend. */
export interface HabilitationRendue {
  readonly code: string;
  readonly libelle: string;
  readonly categorie?: string | null;
  readonly aJustificatif: boolean;
}

/** Une fiche technicien telle que le serveur la rend. */
export interface Technicien {
  readonly id: string;
  readonly nom: string;
  readonly email: string;
  readonly telephone?: string | null;
  readonly dateEmbauche?: string | null;
  readonly role: string;
  readonly roleLibelle: string;
  readonly societe?: string | null;
  readonly actif: boolean;
  readonly compteId?: string | null;
  /**
   * Ce que devient le lien vers le compte, décidé par le SERVEUR :
   * `aucun` (invitation en attente), `actif`, `suspendu` ou `archive`.
   * L'écran affiche `compteEtatLibelle` tel quel, il ne le déduit pas.
   */
  readonly compteEtat?: 'aucun' | 'actif' | 'suspendu' | 'archive';
  readonly compteEtatLibelle?: string;
  readonly creeLe: string;
  readonly photo?: DocumentJoint | null;
  readonly habilitations: ReadonlyArray<HabilitationRendue>;
  readonly nombreHabilitations: number;
}

/** L'invitation qui accompagne la fiche — le lien n'est rendu qu'une fois. */
export interface InvitationDuTechnicien {
  readonly id: string;
  readonly email: string;
  readonly expireLe: string;
  readonly jeton: string;
  readonly lien: string;
}

export interface CreationTechnicien {
  readonly technicien: Technicien;
  readonly invitation: InvitationDuTechnicien;
}

/** Les listes fermées du formulaire : elles viennent du DOMAINE, pas de l'écran. */
export interface References {
  readonly roles: ReadonlyArray<{ readonly code: string; readonly libelle: string }>;
  readonly habilitations: ReadonlyArray<{
    readonly code: string;
    readonly libelle: string;
    readonly categories: ReadonlyArray<string> | null;
  }>;
}

export function listerTechniciens(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<{ items: Technicien[]; total: number }>> {
  return lireJson<{ items: Technicien[]; total: number }>('/api/techniciens', options);
}

export function chargerReferences(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<References>> {
  return lireJson<References>('/api/techniciens/references', options);
}

/**
 * Crée la fiche ET son invitation. Réservé à l'administrateur.
 *
 * Une seule requête : séparer les deux obligerait l'écran à enchaîner deux
 * appels dont le second peut échouer seul, laissant une fiche que personne ne
 * peut activer.
 */
export function creerTechnicien(
  saisie: SaisieTechnicien,
  options: { fetchImpl?: typeof fetch } = {},
): Promise<Resultat<CreationTechnicien>> {
  return ecrireJson<CreationTechnicien>('/api/admin/techniciens', 'POST', saisie, options);
}
